import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { creditLedgerListQuery } from '@xdocs/schemas';
import type { CreditLedger, CreditLedgerListResult, ErrorAlert } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useAuth } from '../../../auth/AuthProvider.js';
import { useWs } from '../../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };

export interface CreditLedgerRow extends CreditLedger {
  /** F19 AC: the running balance shown alongside the ledger is
   * CLIENT-computed, never a stored/returned column. Computed by walking
   * DESC-ordered rows backward from the live `users.creditsBalance`
   * authority (`useCreditsBalance()`), so it stays correct even mid-page —
   * only defined once that live balance has actually loaded. */
  runningBalance: number | null;
}

export interface UseCreditLedgerResult {
  rows: CreditLedgerRow[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/** F19 ledger table — server-side pagination over `credits.ledger`, same
 * `<DataTable>`-bridging shape as `useSharesList`. */
export function useCreditLedger(currentBalance: number | null): UseCreditLedgerResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<CreditLedger[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected' && userId !== null;
  const queryKey = useMemo(() => ['user', userId, 'credits', 'ledger', query] as const, [userId, query]);

  const result = useQuery<CreditLedgerListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'credits.ledger', creditLedgerListQuery.parse(query)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  useEffect(() => {
    // Ledger writes push `user:{userId}` — refetch page 1 (never append) so
    // a live purchase/debit doesn't leave a stale first page while the user
    // browses further down the list.
    if (!enabled) return;
    void queryClient.invalidateQueries({ queryKey: ['user', userId, 'credits', 'ledger'] });
    // Only re-run this specific effect on balance changes (a fresh
    // `creditsBalanceChangedPush`), not on every query/queryClient identity
    // change — those are already covered by `result`'s own queryKey deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBalance]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  // DESC order (newest first), unfiltered, starting from page 1: row[0]'s
  // balance-after equals the live balance; each older row's balance = the
  // next-newer row's balance minus that next-newer row's delta. Stays valid
  // across "Load more" appends (still contiguous, just a longer prefix of
  // the same newest-first list) — but NOT under `dir:'asc'` (no known
  // anchor point), an active search (`q`, which skips non-matching rows and
  // breaks contiguity), or a desktop numbered-pager jump straight to
  // page > 1 (replaces `rows` with a non-contiguous slice). Those cases
  // intentionally leave `runningBalance: null` (E19: an unknown value
  // renders as unknown, never a silently wrong number).
  const jumpedToLaterPage = query.page > 1 && !appendModeRef.current;
  const canAnchorToLiveBalance = query.dir === 'desc' && !query.q && !jumpedToLaterPage;
  let runningBalance = canAnchorToLiveBalance ? currentBalance : null;
  const withRunningBalance: CreditLedgerRow[] = rows.map((row) => {
    const balanceAtRow = runningBalance;
    if (runningBalance !== null) runningBalance -= row.delta;
    return { ...row, runningBalance: balanceAtRow };
  });

  return {
    rows: withRunningBalance,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
  };
}
